import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/AuthContext'
import { Bell, X } from 'lucide-react'
import { format } from 'date-fns'

const TYPE_ICONS = {
  friend_request:   '👋',
  friend_accepted:  '🌿',
  challenge_join:   '🏆',
  eco_milestone:    '🌍',
  new_message:      '💬',
  mention:          '@',
  group_added:      '👥',
  live_started:     '🔴',
  call_incoming:    '📞',
  post_like:        '❤️',
  post_comment:     '💬',
  flick_like:       '🎬',
  flick_comment:    '🎬',
}

const TYPE_COLORS = {
  friend_request:   'bg-blue-100 text-blue-700',
  friend_accepted:  'bg-green-100 text-green-700',
  challenge_join:   'bg-amber-100 text-amber-700',
  eco_milestone:    'bg-emerald-100 text-emerald-700',
  new_message:      'bg-purple-100 text-purple-700',
  mention:          'bg-amber-100 text-amber-700',
  group_added:      'bg-purple-100 text-purple-700',
  live_started:     'bg-red-100 text-red-700',
  call_incoming:    'bg-green-100 text-green-700',
  post_like:        'bg-red-100 text-red-700',
  post_comment:     'bg-blue-100 text-blue-700',
  flick_like:       'bg-pink-100 text-pink-700',
  flick_comment:    'bg-indigo-100 text-indigo-700',
}

export default function NotificationsBell() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    const escHandler = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', escHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', escHandler)
    }
  }, [])

  // Lock body scroll when open (mobile)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  const { data: notifications = [] } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20)
      if (error) throw error
      return data
    },
    enabled: !!user,
    refetchInterval: 30000,
  })

  useEffect(() => {
    if (!user) return
    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['notifications', user.id] })
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [user, queryClient])

  const unreadCount = notifications.filter((n) => !n.read).length

  const markAllRead = async () => {
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false)
    queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] })
  }

  const markRead = async (id) => {
    await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
    queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] })
  }

  const handleNotificationClick = async (n) => {
    await markRead(n.id)
    setOpen(false)

    const data = n.data || {}

    switch (n.type) {
      case 'friend_request':
        navigate('/friends')
        break
      case 'friend_accepted':
        if (data.to_id) navigate(`/profile/${data.to_id}`)
        else navigate('/friends')
        break
      case 'challenge_join':
        navigate('/challenges')
        break
      case 'new_message':
        navigate('/messages')
        break
      case 'mention':
      case 'group_added':
        navigate('/messages')
        break
      case 'live_started':
        if (data.live_id) navigate(`/live/${data.live_id}`)
        else navigate('/live')
        break
      case 'call_incoming':
        // The IncomingCallModal handles ringing calls in-app; for old notifs
        // (or if the call already ended) just go to call history.
        if (data.call_id) navigate(`/call/${data.call_id}`)
        else navigate('/messages')
        break
      case 'post_like':
      case 'post_comment':
        if (data.post_id) navigate(`/feed?post=${data.post_id}`)
        else navigate('/feed')
        break
      case 'flick_like':
      case 'flick_comment':
        if (data.flick_id) navigate(`/flicks?id=${data.flick_id}`)
        else navigate('/flicks')
        break
      case 'eco_milestone':
        navigate('/dashboard')
        break
      default:
        navigate('/dashboard')
    }
  }

  return (
    <>
      {/* Bell button */}
      <button
        onClick={() => setOpen(true)}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative p-2 rounded-xl text-gray-500 hover:bg-white/50 hover:text-green-700 transition-all"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Backdrop + Panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 animate-fade-in"
            onClick={() => setOpen(false)}
          />

          {/* Panel — slides in from right on desktop, full-screen on mobile */}
          <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-label="Notifications"
            className="fixed z-50 bg-white shadow-2xl flex flex-col
              inset-x-0 bottom-0 top-16 rounded-t-3xl
              md:inset-auto md:top-20 md:right-4 md:bottom-auto md:w-96 md:max-h-[calc(100vh-100px)] md:rounded-3xl
              animate-slide-up"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-800 text-lg">Notifications</h3>
                {unreadCount > 0 && (
                  <p className="text-xs text-gray-400">
                    {unreadCount} unread
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {notifications.some((n) => !n.read) && (
                  <button
                    onClick={markAllRead}
                    className="text-xs text-green-600 hover:underline font-medium"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                  className="p-1.5 rounded-xl text-gray-400 hover:bg-gray-100"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {notifications.length === 0 ? (
                <div className="text-center py-16 text-gray-400 px-6">
                  <div className="text-5xl mb-3 opacity-50">🔔</div>
                  <p className="font-medium text-gray-500">All caught up!</p>
                  <p className="text-sm mt-1">No notifications yet.</p>
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex items-start gap-3 px-5 py-4 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${
                      !n.read ? 'bg-green-50/50' : ''
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0 ${
                      TYPE_COLORS[n.type] || 'bg-gray-100'
                    }`}>
                      {TYPE_ICONS[n.type] || '🔔'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
                        {n.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {format(new Date(n.created_at), 'MMM d · h:mm a')}
                      </p>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full bg-green-500 shrink-0 mt-2" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  )
}